// src/components/Notice.jsx
// エラー・完了・お知らせの出し方をそろえる部品【2026-11-02】
//
// もとは画面ごとに赤・緑の枠を手書きしていて、出る場所も見た目も少しずつ違った。
//   <Notice kind="error">{err}</Notice>          … その場に出す。× で閉じられる（文言が変わればまた出る）
//   <Notice kind="error" compact>{err}</Notice>  … 枠を小さく（フォームの中など）
//   <Notice kind="success" toast>{msg}</Notice>  … 右下に数秒だけ出す（トースト）。その場には何も出さない
//   kind は error / success / info / warn。中身が空なら何も出さない。
// コードから直接出すときは toast.success('保存しました') / toast.error('…')。
// トーストを出す場所（<ToastHost />）は Layout.jsx に置いてある。

import React, { useEffect, useRef, useState } from 'react';

const KIND = {
  error:   { icon: '!', label: 'エラー' },
  success: { icon: '✓', label: '完了' },
  info:    { icon: 'i', label: 'お知らせ' },
  warn:    { icon: '!', label: '注意' },
};

export default function Notice({ kind = 'error', toast: asToast = false, compact = false, onClose, children, style }) {
  const empty = children == null || children === false || children === '';
  const [hidden, setHidden] = useState(false);
  // 文言が変わったら（同じ文言をもう一度出したときも、いったん空になってから入るので）また出す
  useEffect(() => { setHidden(false); }, [children]);

  useEffect(() => {
    if (!asToast || empty) return;
    pushToast(kind, children);
  }, [asToast, children]); // eslint-disable-line react-hooks/exhaustive-deps -- 文言が変わったときだけ出す

  if (asToast || empty || hidden) return null;
  const k = KIND[kind] || KIND.info;
  const close = () => { setHidden(true); if (onClose) onClose(); };
  return (
    <div className={'notice notice-' + kind + (compact ? ' notice-compact' : '')}
         role={kind === 'error' || kind === 'warn' ? 'alert' : 'status'} style={style}>
      <span className="notice-icon" aria-hidden="true">{k.icon}</span>
      <div className="notice-body">{children}</div>
      <button type="button" className="notice-close" onClick={close} aria-label={k.label + 'を閉じる'} title="閉じる">×</button>
    </div>
  );
}

// ── トースト ─────────────────────────────────────────────
let seq = 0;
const listeners = new Set();
function pushToast(kind, content) {
  const item = { id: ++seq, kind, content };
  listeners.forEach(fn => fn(item));
}
export const toast = {
  success: (c) => pushToast('success', c),
  error:   (c) => pushToast('error', c),
  info:    (c) => pushToast('info', c),
  warn:    (c) => pushToast('warn', c),
};

// 文字数に合わせて出しておく時間を決める（長い文は読み終わるまで待つ）。エラーは長め
function durationFor(item) {
  const len = typeof item.content === 'string' ? item.content.length : 40;
  const base = item.kind === 'error' ? 7000 : 4000;
  return Math.min(base + len * 60, 15000);
}

export function ToastHost() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const fn = (item) => setItems(list => {
      // 同じ文言が続けて来たら、古いほうを消して出し直す
      const rest = list.filter(x => !(x.kind === item.kind && typeof x.content === 'string' && x.content === item.content));
      return [...rest, item].slice(-4);
    });
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  const remove = (id) => setItems(list => list.filter(x => x.id !== id));
  return (
    <div className="toast-host" aria-live="polite">
      {items.map(it => <ToastItem key={it.id} item={it} onDone={() => remove(it.id)} />)}
    </div>
  );
}

function ToastItem({ item, onDone }) {
  const timer = useRef(null);
  const start = () => { stop(); timer.current = setTimeout(onDone, durationFor(item)); };
  const stop = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  useEffect(() => { start(); return stop; }, []); // eslint-disable-line react-hooks/exhaustive-deps -- 出たときに 1 回だけ
  const k = KIND[item.kind] || KIND.info;
  return (
    // カーソルを乗せている間は消さない
    <div className={'toast toast-' + item.kind} role={item.kind === 'error' ? 'alert' : 'status'}
         onMouseEnter={stop} onMouseLeave={start}>
      <span className="notice-icon" aria-hidden="true">{k.icon}</span>
      <div className="notice-body">{item.content}</div>
      <button type="button" className="notice-close" onClick={onDone} aria-label="閉じる" title="閉じる">×</button>
    </div>
  );
}
